import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, Download, ShieldCheck } from 'lucide-react';
import { useEffect, useState, type FormEvent, type InputHTMLAttributes } from 'react';
import { api, ApiError } from '../lib/api.ts';
import { sessionKey, useSession } from '../lib/session.ts';
import { Button } from './ui/button.tsx';
import { Dialog } from './ui/dialog.tsx';
import { Field, Input } from './ui/form.tsx';
import { Alert, Card } from './ui/misc.tsx';
import { useToast } from './ui/toast.tsx';

type Status = { enabled: boolean; recoveryCodesLeft: number };
const statusKey = ['two-factor'] as const;
const errorText = (err: unknown) => (err instanceof ApiError ? err.message : String(err));

/** The 6-digit code box: numeric keyboard on phones, and the OS can fill it from the app. */
function CodeInput({
  value,
  onChange,
  ...rest
}: { value: string; onChange: (v: string) => void } & Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange'
>) {
  return (
    // `rest` carries the id and aria attributes <Field> adds, so the label points here.
    <Input
      {...rest}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      inputMode="numeric"
      autoComplete="one-time-code"
      maxLength={20}
      className="font-mono tracking-widest"
    />
  );
}

/** The QR code for the authenticator app, drawn only when setting up (uqr loads on demand). */
function QrImage({ value }: { value: string }) {
  const [svg, setSvg] = useState('');
  useEffect(() => {
    let live = true;
    void import('uqr').then(({ renderSVG }) => {
      if (live) setSvg(renderSVG(value, { border: 2, whiteColor: '#ffffff', blackColor: '#000000' }));
    });
    return () => {
      live = false;
    };
  }, [value]);
  return (
    <div
      role="img"
      aria-label="QR code to scan with your authenticator app"
      className="mx-auto size-48 [&_svg]:size-full"
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}

function RecoveryCodes({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const text = `NABOCADO Helpdesk recovery codes (each works once)\n\n${codes.join('\n')}\n`;
  return (
    <div className="space-y-4">
      <Alert tone="warning" title="Save these recovery codes now">
        If you lose your phone, each code lets you sign in once instead of the app’s code. You won’t see them
        again. Keep them somewhere safe, away from your phone: printed, or in a password manager.
      </Alert>
      <ul className="grid grid-cols-2 gap-2 rounded-lg bg-surface-2 p-4 font-mono text-sm">
        {codes.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ul>
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="secondary"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(text);
              setCopied(true);
            } catch {
              // Clipboard blocked: download or write them down instead.
            }
          }}
        >
          {copied ? <Check className="size-4" aria-hidden /> : <Copy className="size-4" aria-hidden />}
          {copied ? 'Copied' : 'Copy'}
        </Button>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            const a = document.createElement('a');
            a.href = URL.createObjectURL(new Blob([text], { type: 'text/plain' }));
            a.download = 'helpdesk-recovery-codes.txt';
            a.click();
            URL.revokeObjectURL(a.href);
          }}
        >
          <Download className="size-4" aria-hidden />
          Download
        </Button>
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={saved}
          onChange={(e) => setSaved(e.target.checked)}
          className="size-4 accent-(--brand)"
        />
        I’ve saved my recovery codes
      </label>
      <div className="flex justify-end">
        <Button disabled={!saved} onClick={onDone}>
          Done
        </Button>
      </div>
    </div>
  );
}

function SetupDialog({ onClose }: { onClose: () => void }) {
  const client = useQueryClient();
  const toast = useToast();
  const [step, setStep] = useState<'password' | 'scan' | 'codes'>('password');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [setup, setSetup] = useState<{ secret: string; otpauthUrl: string }>();
  const [codes, setCodes] = useState<string[]>([]);
  const [error, setError] = useState<string>();

  const begin = useMutation({
    mutationFn: () =>
      api.post<{ secret: string; otpauthUrl: string }>('/api/me/two-factor/setup', { password }),
    onSuccess: (s) => {
      setSetup(s);
      setError(undefined);
      setStep('scan');
    },
    onError: (err) => setError(errorText(err)),
  });
  const enable = useMutation({
    mutationFn: () => api.post<{ recoveryCodes: string[] }>('/api/me/two-factor/enable', { code }),
    onSuccess: ({ recoveryCodes }) => {
      setCodes(recoveryCodes);
      setError(undefined);
      setStep('codes');
      void client.invalidateQueries({ queryKey: statusKey });
      void client.invalidateQueries({ queryKey: sessionKey });
    },
    onError: (err) => setError(errorText(err)),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (step === 'password') begin.mutate();
    else if (step === 'scan') enable.mutate();
  };

  return (
    <Dialog open onClose={step === 'codes' ? () => undefined : onClose} title="Turn on two-step sign-in">
      {step === 'codes' ? (
        <RecoveryCodes
          codes={codes}
          onDone={() => {
            toast({ tone: 'success', title: 'Two-step sign-in is on' });
            onClose();
          }}
        />
      ) : (
        <form onSubmit={submit} noValidate className="space-y-4">
          {error && <Alert tone="error">{error}</Alert>}
          {step === 'password' ? (
            <>
              <p className="text-sm text-muted">
                You’ll need an authenticator app on your phone, such as Google Authenticator or Microsoft
                Authenticator. First, confirm it’s you.
              </p>
              <Field label="Your password">
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  autoFocus
                />
              </Field>
            </>
          ) : (
            <>
              <p className="text-sm">
                <strong>1.</strong> In your authenticator app, add an account and scan this code.
              </p>
              <QrImage value={setup!.otpauthUrl} />
              <p className="text-center text-xs text-muted">
                Can’t scan it? Enter this key instead:
                <br />
                <span className="font-mono text-sm break-all text-text">
                  {setup!.secret.replace(/(.{4})/g, '$1 ').trim()}
                </span>
              </p>
              <Field label="2. Enter the 6-digit code the app shows">
                <CodeInput value={code} onChange={setCode} autoFocus />
              </Field>
            </>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={begin.isPending || enable.isPending}>
              {step === 'password' ? 'Continue' : 'Turn on'}
            </Button>
          </div>
        </form>
      )}
    </Dialog>
  );
}

function NewCodesDialog({ onClose }: { onClose: () => void }) {
  const client = useQueryClient();
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[]>();
  const [error, setError] = useState<string>();
  const renew = useMutation({
    mutationFn: () => api.post<{ recoveryCodes: string[] }>('/api/me/two-factor/recovery-codes', { code }),
    onSuccess: ({ recoveryCodes }) => {
      setCodes(recoveryCodes);
      void client.invalidateQueries({ queryKey: statusKey });
    },
    onError: (err) => setError(errorText(err)),
  });
  return (
    <Dialog open onClose={codes ? () => undefined : onClose} title="New recovery codes">
      {codes ? (
        <RecoveryCodes codes={codes} onDone={onClose} />
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            renew.mutate();
          }}
          noValidate
          className="space-y-4"
        >
          {error && <Alert tone="error">{error}</Alert>}
          <p className="text-sm text-muted">Your old recovery codes will stop working.</p>
          <Field label="Code from your authenticator app">
            <CodeInput value={code} onChange={setCode} autoFocus />
          </Field>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={renew.isPending}>
              Make new codes
            </Button>
          </div>
        </form>
      )}
    </Dialog>
  );
}

function DisableDialog({ onClose }: { onClose: () => void }) {
  const client = useQueryClient();
  const toast = useToast();
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string>();
  const disable = useMutation({
    mutationFn: () => api.post('/api/me/two-factor/disable', { password, code }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: statusKey });
      void client.invalidateQueries({ queryKey: sessionKey });
      toast({ tone: 'success', title: 'Two-step sign-in is off' });
      onClose();
    },
    onError: (err) => setError(errorText(err)),
  });
  return (
    <Dialog open onClose={onClose} title="Turn off two-step sign-in">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          disable.mutate();
        }}
        noValidate
        className="space-y-4"
      >
        {error && <Alert tone="error">{error}</Alert>}
        <p className="text-sm text-muted">Your password alone will then be enough to sign in.</p>
        <Field label="Your password">
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            autoFocus
          />
        </Field>
        <Field label="Code from your authenticator app (or a recovery code)">
          <CodeInput value={code} onChange={setCode} />
        </Field>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="secondary" loading={disable.isPending}>
            Turn off
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/** Settings, for IT accounts: an authenticator-app code after the password. */
export function TwoFactorCard() {
  const { isIt } = useSession();
  const [open, setOpen] = useState<'setup' | 'codes' | 'disable' | null>(null);
  const { data } = useQuery({
    queryKey: statusKey,
    queryFn: () => api.get<Status>('/api/me/two-factor'),
    enabled: isIt,
  });
  if (!isIt || !data) return null;

  return (
    <Card className="p-5 sm:p-6">
      <h2 className="flex items-center gap-2 font-semibold">
        <ShieldCheck className={`size-5 ${data.enabled ? 'text-brand' : 'text-muted'}`} aria-hidden />
        Two-step sign-in {data.enabled ? 'is on' : 'is off'}
      </h2>
      {data.enabled ? (
        <>
          <p className="mt-1 text-sm text-muted">
            After your password, you enter the 6-digit code from your authenticator app. Someone who learns
            your password still can’t sign in without your phone.
          </p>
          <p className={`mt-3 text-sm ${data.recoveryCodesLeft <= 2 ? 'font-medium text-warning' : ''}`}>
            {data.recoveryCodesLeft} recovery {data.recoveryCodesLeft === 1 ? 'code' : 'codes'} left
            {data.recoveryCodesLeft <= 2 && ': make new ones soon.'}
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button variant="secondary" onClick={() => setOpen('codes')}>
              New recovery codes
            </Button>
            <Button variant="ghost" onClick={() => setOpen('disable')}>
              Turn off
            </Button>
          </div>
        </>
      ) : (
        <>
          <p className="mt-1 text-sm text-muted">
            Your account can see every ticket and everyone’s details. With two-step sign-in, a stolen or
            guessed password isn’t enough: signing in also needs a code from an app on your phone.
          </p>
          <Button className="mt-4" onClick={() => setOpen('setup')}>
            Turn on two-step sign-in
          </Button>
        </>
      )}
      {open === 'setup' && <SetupDialog onClose={() => setOpen(null)} />}
      {open === 'codes' && <NewCodesDialog onClose={() => setOpen(null)} />}
      {open === 'disable' && <DisableDialog onClose={() => setOpen(null)} />}
    </Card>
  );
}
